/* @jsxImportSource react */
'use client';

import { forwardRef, type ReactNode, useCallback, useId } from 'react';

import type { CheckboxIndicatorState, ComponentRenderFn, HTMLProps } from '@base-ui/react';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { useObservability } from '@opendoor/observability/slim';
import { IconCheck, IconMinus } from '@tabler/icons-react';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import {
  type AccessibleNameWith,
  hasAccessibleName,
  useAccessibleNameConflictWarning,
  useAccessibleNameWarning,
} from '../../utils/accessible-name';
import { cn } from '../../utils/cn';

/**
 * Event details passed to onCheckedChange callback. Re-exported from Base UI.
 *
 * @public
 */
export type CheckboxChangeEventDetails = Parameters<
  NonNullable<BaseCheckbox.Root.Props['onCheckedChange']>
>[1];

/**
 * Checkbox variant definitions mapping variant names to their Tailwind classes.
 * The component consumes these directly (see the control's `cn(...)` below), so
 * the registry documents the classes that actually render.
 *
 * @internal
 */
export const ATOMS_CHECKBOX_VARIANTS = {
  variant: {
    default: {
      classes:
        'ring-od-border-primary data-checked:bg-od-bg-inverse-primary data-checked:ring-od-border-focused-primary data-indeterminate:bg-od-bg-inverse-primary data-indeterminate:ring-od-border-focused-primary',
      description: 'Default checkbox appearance',
    },
    error: {
      classes:
        'ring-od-border-error data-checked:bg-od-bg-error data-checked:ring-od-border-error data-indeterminate:bg-od-bg-error data-indeterminate:ring-od-border-error',
      description: 'Error state for validation failures',
    },
  },
  size: {
    xs: {
      classes: 'h-5 w-5 rounded-md',
      description: '20px box',
    },
    sm: {
      classes: 'h-6 w-6 rounded-md',
      description: '24px box',
    },
    md: {
      classes: 'h-7 w-7 rounded-md',
      description: '28px box (default)',
    },
  },
} as const;

/** @internal */
export const ATOMS_CHECKBOX_DEFAULT_VARIANTS = {
  variant: 'default',
  size: 'md',
} as const;

/** @public */
export type AtomsCheckboxVariant = keyof typeof ATOMS_CHECKBOX_VARIANTS.variant;
/** @public */
export type AtomsCheckboxSize = keyof typeof ATOMS_CHECKBOX_VARIANTS.size;

/** @public */
export interface AtomsCheckboxVariantsProps {
  /**
   * Visual variant.
   * - `"default"` — Standard checkbox appearance
   * - `"error"` — Error state for validation failures
   * @default "default"
   */
  variant?: AtomsCheckboxVariant;
  /**
   * Size variant.
   * - `"xs"` — 20px box
   * - `"sm"` — 24px box
   * - `"md"` — 28px box (default)
   * @default "md"
   */
  size?: AtomsCheckboxSize;
}

/** @public */
export function checkboxVariants({
  variant = ATOMS_CHECKBOX_DEFAULT_VARIANTS.variant,
  size = ATOMS_CHECKBOX_DEFAULT_VARIANTS.size,
}: AtomsCheckboxVariantsProps = {}) {
  return cn(
    ATOMS_CHECKBOX_VARIANTS.variant[variant].classes,
    ATOMS_CHECKBOX_VARIANTS.size[size].classes,
  );
}

/** @public */
export type CheckboxVariant = AtomsCheckboxVariant;
/** @public */
export type CheckboxSize = AtomsCheckboxSize;

// Per-size icon sizes; the box classes come straight from
// ATOMS_CHECKBOX_VARIANTS.size so a resize can't drift between the two.
const ICON_SIZES: Record<AtomsCheckboxSize, number> = {
  xs: 14,
  sm: 16,
  md: 18,
};

/** Checkbox props that don't bear on the accessible name. */
export type CheckboxBaseProps = Omit<AnalyticsProps, 'analyticsName'> & {
  /** Visual variant: "default" or "error" for validation failures */
  variant?: CheckboxVariant;
  /** Size: `xs` (20px), `sm` (24px), or `md` (28px, default). */
  size?: CheckboxSize;
  /** Whether the checkbox is checked (controlled) */
  checked?: boolean;
  /** Whether the checkbox is in indeterminate state */
  indeterminate?: boolean;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Callback when the checked state changes */
  onCheckedChange?: BaseCheckbox.Root.Props['onCheckedChange'];
  /** Called when the checkbox loses focus — `Form.Checkbox` wires touched state here. */
  onBlur?: BaseCheckbox.Root.Props['onBlur'];
  /** Name for form submission */
  name?: string;
  /** Additional class name applied to the checkbox box */
  className?: string;
};

/**
 * Accessible-name requirement. A checkbox must be named one of four ways:
 * - `caption` — label text rendered to the right of the box, as a sibling
 *   `<label htmlFor>` tied to the control's `id`. Clicking it toggles the box,
 *   and it provides the accessible name. Prefer this for a self-labeled box.
 * - `aria-label` — accessible label when no visible label text is rendered.
 * - `aria-labelledby` — id of an external element that labels this checkbox.
 * - `id` — forwarded to the control and paired with an external
 *   `<label htmlFor={id}>` (e.g. the association `Field`/`Form` set up).
 *
 * One is required at the type level so a checkbox can never ship unnamed; the
 * exclusivity rules come from the shared {@link AccessibleNameWith} builder so
 * they can't drift from the other controls'.
 */
type CheckboxAccessibleNameProps = AccessibleNameWith<{ caption: ReactNode }>;

export type CheckboxAnalyticsProps =
  | {
      /** Value for form submission and the analytics event's non-PII `optionName`. */
      value: string;
      /** Names this checkbox in Journey events. Convention: `{appName}-{componentName}`. Requires `value` when set. */
      analyticsName?: string;
    }
  | {
      analyticsName?: never;
      value?: never;
    };

export type CheckboxProps = CheckboxBaseProps &
  CheckboxAccessibleNameProps &
  CheckboxAnalyticsProps;

/**
 * Boolean (or tri-state) selection control. Pass `caption` to render label text
 * to the right of the box (clicking it toggles, and it names the control for
 * assistive tech); otherwise give it an accessible name via `aria-label` /
 * `aria-labelledby`. Use the `error` variant to surface validation failures and
 * `indeterminate` for "some but not all" parent/child selections.
 *
 * State is controlled via `checked` + `onCheckedChange`; `name`/`value` wire it
 * up for native form submission.
 *
 * @example
 * ```tsx
 * <Checkbox checked={checked} onCheckedChange={setChecked} caption="Accept terms and conditions" />
 *
 * <Checkbox variant="error" size="sm" aria-label="Required option" />
 * <Checkbox indeterminate aria-label="Select all" />
 * ```
 */
export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      className,
      checked,
      indeterminate,
      disabled,
      variant = 'default',
      size = 'md',
      onCheckedChange,
      name,
      value,
      caption,
      id,
      analyticsName,
      trackImpression,
      ...props
    },
    ref,
  ) => {
    const iconSize = ICON_SIZES[size];
    const { trackJourneyEvent } = useObservability();
    const viewRef = useImpressionRef(
      Boolean(analyticsName && trackImpression),
      analyticsName,
      'checkboxViewEvent',
      ref,
    );

    useAccessibleNameWarning('Checkbox', hasAccessibleName({ ...props, id }, caption != null));
    useAccessibleNameConflictWarning(
      'Checkbox',
      caption != null && Boolean(props['aria-label'] || props['aria-labelledby']),
    );

    const renderIndicator = useCallback<ComponentRenderFn<HTMLProps, CheckboxIndicatorState>>(
      (renderProps, state) => (
        <span {...renderProps}>
          {state.indeterminate ? (
            <IconMinus stroke={2.5} size={iconSize} />
          ) : (
            <IconCheck stroke={2.5} size={iconSize} />
          )}
        </span>
      ),
      [iconSize],
    );

    // Base UI invokes onCheckedChange only on genuine user interaction, not on
    // controlled/programmatic `checked` updates — so the click event is safe to
    // emit from here. Guarded: no event without an analyticsName.
    const handleCheckedChange = useCallback<NonNullable<CheckboxProps['onCheckedChange']>>(
      (nextChecked, eventDetails) => {
        const optionName = value?.trim();
        if (analyticsName && optionName) {
          trackJourneyEvent('platform', 'checkboxClickEvent', {
            targetName: analyticsName,
            optionName,
          });
        }
        onCheckedChange?.(nextChecked, eventDetails);
      },
      [analyticsName, onCheckedChange, value, trackJourneyEvent],
    );

    // Ties the caption's (or an external) `<label htmlFor>` to the control.
    // Generated only when a caption needs one; an explicit `id` always wins.
    const generatedId = useId();
    const controlId = id ?? (caption != null ? generatedId : undefined);

    const box = (
      <BaseCheckbox.Root
        ref={viewRef}
        id={controlId}
        name={name}
        value={value}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
        className={cn(
          'relative flex items-center justify-center border-0 ring-1 focus:outline-none after:absolute after:-inset-x-3 after:-inset-y-2',
          ATOMS_CHECKBOX_VARIANTS.size[size].classes,
          // Disabled wins over variant + checked state — gray bg, default border, no hover/focus
          disabled
            ? 'bg-od-bg-hover-primary ring-od-border-primary cursor-not-allowed'
            : [
                'bg-od-bg-primary cursor-pointer hover:ring-od-border-hover-primary focus-visible:ring-od-border-focused-accent-brand',
                ATOMS_CHECKBOX_VARIANTS.variant[variant].classes,
              ],
          className,
        )}
        {...props}
      >
        <BaseCheckbox.Indicator
          keepMounted
          className={cn(
            'flex items-center justify-center data-unchecked:invisible',
            // Disabled+checked: muted-but-visible check on the grey box (the old
            // inactive-secondary token sat at ~1.3:1 against the disabled bg).
            disabled ? 'text-od-content-tertiary' : 'text-od-content-inverse-primary',
          )}
          render={renderIndicator}
        />
      </BaseCheckbox.Root>
    );

    if (caption == null) {
      return box;
    }

    // The caption is a sibling label associated explicitly via htmlFor/id — the
    // same mechanism Field uses — rather than an implicit wrapping <label>.
    return (
      <span className="inline-flex items-center gap-3">
        {box}
        <label
          htmlFor={controlId}
          className={cn(
            'text-body-md text-od-content-primary',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          {caption}
        </label>
      </span>
    );
  },
);

Checkbox.displayName = 'Checkbox';
